import React, { ReactElement } from 'react';
import { TextField, IconButton, Stack, Spinner, SpinnerSize, Text, IColumn } from '@fluentui/react';
import { ActivityForEditing } from '../types/ActivityTypes';

interface ActivityTableRowProps {
    item: ActivityForEditing;
    column: IColumn;
    onUpdateItem: (identifier: string, field: keyof ActivityForEditing, value: any) => void;
    onSaveRecord: (activity: ActivityForEditing) => void;
    onDeleteRecord: (activity: ActivityForEditing) => void;
}

export const ActivityTableRow: React.FC<ActivityTableRowProps> = ({ item, column, onUpdateItem, onSaveRecord, onDeleteRecord }) => {
    const itemId = item.isNew ? item.tempId! : item.id!;

    const renderNameField = (): ReactElement => (
        <TextField
            value={item.name || ''}
            onChange={(_, val) => onUpdateItem(itemId, 'name', val ?? '')}
            styles={{ root: { marginRight: 32 } }}
            placeholder="Activity name"
        />
    );

    const renderCurrencyField = (): ReactElement => (
        <TextField
            value={item.rateCurrencyCode || ''}
            onChange={(_, val) => onUpdateItem(itemId, 'rateCurrencyCode', val ?? '')}
            styles={{ root: { marginRight: 32 } }}
            placeholder="Currency code"
        />
    );

    const renderRateField = (): ReactElement => (
        <TextField
            type="number"
            value={item.rate?.toString() || ''}
            onChange={(_, val) => onUpdateItem(itemId, 'rate', val ? parseFloat(val) : null)}
            styles={{ root: { marginRight: 32 } }}
            placeholder="0.00"
        />
    );

    const renderActionsField = (): ReactElement => {
        const isSaving = item.isSaving || false;
        const isDeleting = item.isDeleting || false;

        return (
            <Stack horizontal tokens={{ childrenGap: 8 }}>
                {isSaving ? (
                    <Spinner size={SpinnerSize.small} />
                ) : item.justSaved ? (
                    <IconButton iconProps={{ iconName: 'CheckMark', style: { color: '#107c10' } }} title="Saved successfully" disabled />
                ) : (
                    <IconButton
                        iconProps={{ iconName: 'Save' }}
                        title="Save"
                        onClick={() => onSaveRecord(item)}
                        disabled={!item.name?.trim() || isSaving || isDeleting}
                    />
                )}
                <IconButton iconProps={{ iconName: 'Delete' }} title="Delete" onClick={() => onDeleteRecord(item)} disabled={isSaving || isDeleting} />
            </Stack>
        );
    };

    switch (column.key) {
        case 'name':
            return renderNameField();
        case 'rateCurrencyCode':
            return renderCurrencyField();
        case 'rate':
            return renderRateField();
        case 'actions':
            return renderActionsField();
        default:
            return <Text>{(item as any)[column.fieldName]}</Text>;
    }
};
